import { queries } from "@testing-library/react";
import { ICompletionStatus } from "../ICompletionStatus";
import { IQuestionnaireQuestion, QuestionEvidenceSource } from "./IQuestionnaireQuestion";
import { IQuestionnaireSectionResults } from "./IQuestionnaireSectionResults";
import { IQuestionnaireSection } from "./IQuestionnaireSection";
import { IGrade } from "../IGrade";
import { IDynamicField, SpecialField } from "./IDynamicField";
import { ISelectOption } from "../../Components/Utils/MultiSelect/MultiSelect";
import { IDropDownListItem } from "../DropDownList/IDropDownListItem";
import { CurateResource } from "../Curate/CurateResource";

export type ICriteriaItem = {
    id: string,
    title: string,
    description: string,
    active: boolean,
    questions: IQuestionnaireQuestion[],
    grade: IGrade,
    tags: string[],
    tagsData: ISelectOption[],
    date: Date | undefined,
    fields: IDynamicField[],
    generatedResults: boolean,
    /** Distinct evidence used for answers when the summary was generated. */
    summaryEvidenceSources?: QuestionEvidenceSource[],
    /** Curate tiles recommended when the summary was generated. */
    recommendedCurateResources?: CurateResource[],
    fullCriteria: string,
    picklistData: ISelectOption[],
    picklist: ISelectOption[],
    dropdownData: IDropDownListItem[],
    dropdown: string,
    personData: IDropDownListItem[],
    person: string[]
}

export const getQuestionIndex = (criteriaItem: ICriteriaItem, question: IQuestionnaireQuestion): number => {
    return criteriaItem.questions.findIndex((q) => q.id === question.id);
};

export const getSectionIndex = (sections: IQuestionnaireSection[], question: IQuestionnaireQuestion): number => {
    return sections.findIndex((section) =>
        getCriteriaIndex(section, question) !== -1
    );
};

export const getSectionIndexById = (sections: IQuestionnaireSection[], sectionId: string): number => {
    return sections.findIndex((section) =>
        section.id === sectionId
    );
};

export const getCriteriaIndex = (section: IQuestionnaireSection, question: IQuestionnaireQuestion): number => {
    return section.criteria.findIndex((criteriaItem) => 
        getQuestionIndex(criteriaItem, question) !== -1
    );
};

export const getCriteriaIndexById = (section: IQuestionnaireSection, criteriaId: string): number => {
    return section.criteria.findIndex((criteriaItem) => 
        criteriaItem.id === criteriaId
    );
};

export const getAreasForImprovement = (fields: IDynamicField[]): IDynamicField[] => {
    return fields.filter(field => field.specialField === SpecialField.AreasForImprovement);
};

export const distinctEvidenceSources = (
    sources: QuestionEvidenceSource[] | undefined
): QuestionEvidenceSource[] => {
    if (!sources?.length) {
        return [];
    }

    const seen = new Set<string>();
    return sources.filter((source) => {
        const key = source.id || `${source.portfolioId}:${source.folderId}:${source.fileName}`;
        if (!key || seen.has(key)) {
            return false;
        }
        seen.add(key);
        return !!source.fileName;
    });
};

export const evidenceSourcesFromQuestions = (
    questions: IQuestionnaireQuestion[] | undefined
): QuestionEvidenceSource[] => {
    return distinctEvidenceSources(
        questions
            ?.filter((question) => !!question.answer?.trim())
            .flatMap((question) => question.evidenceSources ?? [])
    );
};
